import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { NavLink } from "react-router-dom";

import TokenCounter from "@/components/smartpublish/TokenCounter";
import BrandSwitcher from "@/components/smartpublish/BrandSwitcher";
import UserAvatarMenu from "@/components/smartpublish/UserAvatarMenu";
import { useSession } from "@/contexts/SessionContext";

const navLinkClass = ({ isActive }) =>
    [
        "relative inline-flex items-center px-2 py-1 text-xs font-medium uppercase tracking-[0.18em] transition-colors",
        isActive ? "text-[#00F5D4]" : "text-[#8A93A6] hover:text-[#E8ECF5]",
    ].join(" ");

export default function TopNav({ onNewPost }) {
    const { isAuthenticated, tier, openSubscriptionDrawer } = useSession();

    const strategyLocked = tier !== "creator" && tier !== "pro";
    const handleStrategyClick = (e) => {
        if (strategyLocked) {
            e.preventDefault();
            openSubscriptionDrawer({
                code: "tier_required",
                required: "creator",
                feature: "Strategy generation",
            });
        }
    };

    return (
        <motion.header
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
            className="sticky top-0 z-40 border-b border-[#1F2A40] bg-[#0B0F19]/80 backdrop-blur-xl"
        >
            <div className="mx-auto flex h-16 w-full max-w-[1100px] items-center justify-between gap-3 px-6">
                <div className="flex min-w-0 items-center gap-5">
                    <div
                        className="flex items-center gap-1 text-base font-medium tracking-wide text-[#E8ECF5]"
                        data-testid="brand-wordmark"
                    >
                        <span className="sp-glow-mint font-bold">S</span>
                        <span>martPublish</span>
                    </div>

                    {isAuthenticated && <BrandSwitcher />}

                    <nav
                        className="flex items-center gap-1"
                        data-testid="primary-nav"
                    >
                        <NavLink
                            to="/"
                            end
                            className={navLinkClass}
                            data-testid="nav-home"
                        >
                            {({ isActive }) => (
                                <>
                                    Home
                                    {isActive && (
                                        <motion.span
                                            layoutId="nav-underline"
                                            className="absolute -bottom-1 left-2 right-2 h-[1.5px] rounded-full bg-[#00F5D4]"
                                            style={{ boxShadow: "0 0 8px rgba(0,245,212,0.6)" }}
                                        />
                                    )}
                                </>
                            )}
                        </NavLink>
                        <NavLink
                            to="/dashboard"
                            className={navLinkClass}
                            data-testid="nav-dashboard"
                        >
                            {({ isActive }) => (
                                <>
                                    Dashboard
                                    {isActive && (
                                        <motion.span
                                            layoutId="nav-underline"
                                            className="absolute -bottom-1 left-2 right-2 h-[1.5px] rounded-full bg-[#00F5D4]"
                                            style={{ boxShadow: "0 0 8px rgba(0,245,212,0.6)" }}
                                        />
                                    )}
                                </>
                            )}
                        </NavLink>
                        <NavLink
                            to="/strategy"
                            onClick={handleStrategyClick}
                            className={({ isActive }) =>
                                [
                                    navLinkClass({ isActive }),
                                    strategyLocked && "opacity-80",
                                ]
                                    .filter(Boolean)
                                    .join(" ")
                            }
                            data-testid="nav-strategy"
                            data-locked={strategyLocked ? "true" : "false"}
                        >
                            {({ isActive }) => (
                                <>
                                    <span className="inline-flex items-center gap-1">
                                        Strategy
                                        {strategyLocked && (
                                            <span
                                                className="ml-1 rounded-full border border-[#00F5D4]/40 bg-[#00F5D4]/10 px-1 py-0.5 font-mono text-[8px] uppercase tracking-[0.22em] text-[#00F5D4]"
                                                data-testid="nav-strategy-pro-badge"
                                            >
                                                pro
                                            </span>
                                        )}
                                    </span>
                                    {isActive && !strategyLocked && (
                                        <motion.span
                                            layoutId="nav-underline"
                                            className="absolute -bottom-1 left-2 right-2 h-[1.5px] rounded-full bg-[#00F5D4]"
                                            style={{ boxShadow: "0 0 8px rgba(0,245,212,0.6)" }}
                                        />
                                    )}
                                </>
                            )}
                        </NavLink>
                    </nav>

                    {onNewPost && (
                        <button
                            type="button"
                            onClick={onNewPost}
                            className="group ml-1 hidden items-center gap-2 rounded-lg border border-[#1F2A40] bg-[#111726] px-3 py-1.5 text-xs font-medium text-[#E8ECF5] transition-all duration-200 hover:border-[#00F5D4]/60 hover:text-[#00F5D4] hover:shadow-[0_0_16px_rgba(0,245,212,0.15)] md:inline-flex"
                            data-testid="new-post-btn"
                        >
                            <Plus className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-90" />
                            New post
                        </button>
                    )}
                </div>

                <div className="flex items-center gap-3">
                    <TokenCounter />
                    {isAuthenticated && <UserAvatarMenu />}
                </div>
            </div>
        </motion.header>
    );
}
